/**
 * Thin client for the browser-facing session endpoints of the IdVerify API (`/v1/session/...`).
 * All calls carry the SMS link's bearer token; errors surface as ApiClientError
 * with the server's stable error code so screens can react (e.g. SESSION_EXPIRED).
 */

import type { StepType } from "@inertiax/kyc-react";

export interface SessionView {
  sessionId: string;
  status: "pending" | "in_progress" | "completed" | "expired" | "cancelled";
  steps: StepId[];
  completedSteps: StepId[];
  /** Liveness sub-challenges to run — fed straight into IdVerification's `steps` prop. */
  livenessChallenges: StepType[];
  expiresAt: string;
  phoneMasked: string;
  /** Verification purpose the merchant configured — drives customer-facing copy. */
  mode: "age" | "id";
  /** Minimum age to pass (age mode only). */
  ageLimit?: number;
}

export type StepId = "liveness" | "id-front" | "id-back" | "face-match";

export interface OcrIdFields {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  documentNumber?: string;
  expiryDate?: string;
}

export interface CompleteResponse {
  status: string;
  decision: "approved" | "declined" | "review";
  /** Stable reason codes explaining a declined/review outcome (e.g. "document_expired"). */
  reasons?: string[];
  webhookDelivered: boolean | null;
  /**
   * Where the merchant wants the customer sent afterwards, with verificationId
   * and decision already appended. Absent/null when the merchant set none.
   */
  redirectUrl?: string | null;
}

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/** How long a single attempt waits before we give up and (maybe) retry. */
const REQUEST_TIMEOUT_MS = 20_000;
/** Extra attempts after the first, for connectivity blips only — never for
 * an HTTP error response, which means the server was reachable and answered. */
const MAX_RETRIES = 2;
const RETRY_BACKOFF_MS = [500, 1500];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class VerifyApi {
  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
  ) {}

  /**
   * A phone that drops to one bar mid-flow must not sit on a spinner forever:
   * every attempt is bounded by `REQUEST_TIMEOUT_MS`, and a connectivity
   * failure (timeout, offline, DNS blip) gets a couple of backed-off retries
   * before surfacing — never a bare HTTP error response, which already means
   * the server was reached and answered.
   */
  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let lastNetworkError: unknown;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(`${this.baseUrl}/v1/session${path}`, {
          ...init,
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${this.token}`,
            ...(init.headers ?? {}),
          },
        });
        const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        if (!response.ok) {
          // IdVerify returns RFC 7807 Problem Details: `errorCode` for the stable
          // machine-readable code, `detail` (falling back to `title`) for the message.
          throw new ApiClientError(
            typeof body.errorCode === "string" ? body.errorCode : "UNKNOWN",
            typeof body.detail === "string"
              ? body.detail
              : typeof body.title === "string"
                ? body.title
                : `HTTP ${response.status}`,
            response.status,
          );
        }
        return body as T;
      } catch (err) {
        if (err instanceof ApiClientError) throw err;
        lastNetworkError = err;
        if (attempt < MAX_RETRIES) await sleep(RETRY_BACKOFF_MS[attempt] ?? 1000);
      } finally {
        clearTimeout(timer);
      }
    }
    const timedOut = lastNetworkError instanceof DOMException && lastNetworkError.name === "AbortError";
    throw new ApiClientError(
      timedOut ? "TIMEOUT" : "NETWORK_ERROR",
      timedOut
        ? "The connection is too slow right now. Check your signal and try again."
        : "We couldn't reach the server. Check your connection and try again.",
      0,
    );
  }

  private json<T>(path: string, payload: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  getSession(): Promise<SessionView> {
    return this.request<SessionView>("");
  }

  /**
   * Nonce-bound liveness (doc HIGH-ASSURANCE §5). Request a single-use nonce;
   * the SDK derives the flash sequence / blink count from it, then the evidence
   * bundle it produces goes to `verifyLiveness` for a server-recomputed verdict.
   */
  getLivenessChallenge(): Promise<{
    nonce: string;
    flashSequence: Array<{ r: number; g: number; b: number }>;
    blinkCount: number;
    expiresInMs: number;
  }> {
    return this.json("/liveness/challenge", {});
  }

  verifyLiveness(evidence: unknown): Promise<{
    ok: boolean;
    verdict: { score: number; passed: boolean; flags: string[] };
    flashScore: number | null;
    reasons: string[];
    signature: string | null;
  }> {
    return this.json("/liveness/verify", { ...(evidence as object) });
  }

  async postIdFront(image: Blob): Promise<{ fields: OcrIdFields }> {
    const form = new FormData();
    form.append("image", image, "id-front.jpg");
    return this.request("/steps/id-front", { method: "POST", body: form });
  }

  /**
   * The photo of the back, plus the on-device PDF417 decode when there is one. IdVerify
   * decodes the photo itself; the text is only its fallback, and is treated as unverified
   * until the front's OCR agrees with it - so the photo is always required.
   */
  async postIdBack(image: Blob, barcodeText?: string): Promise<{ fields: Record<string, string> }> {
    const form = new FormData();
    form.append("image", image, "id-back.jpg");
    if (barcodeText) form.append("barcodeText", barcodeText);
    return this.request("/steps/id-back", { method: "POST", body: form });
  }

  /**
   * The selfie that was compared to the ID. The server decides whether to keep it
   * (`Sessions:StoreSelfie`); either way the call succeeds.
   */
  async postSelfie(image: Blob): Promise<void> {
    const form = new FormData();
    form.append("image", image, "selfie.jpg");
    await this.request("/images/selfie", { method: "POST", body: form });
  }

  postFaceMatch(
    score: number,
    diagnostics: {
      method?: string;
      flags?: string[];
      /** A second model's score, stored as test data only — never part of the decision. */
      shadow?: { method: string; score: number | null; flags: string[] };
    } = {},
  ): Promise<SessionView> {
    return this.json("/steps/face-match", { score, ...diagnostics });
  }

  complete(): Promise<CompleteResponse> {
    return this.json("/complete", {});
  }
}

/**
 * Demo mode: show a "start a verification" screen when the SPA is opened
 * without a session link, instead of dead-ending.
 *
 * Normally this app is only ever reached through the link in the SMS, so a
 * missing token is a real error. On the local stack there is no SMS and no
 * merchant backend to create sessions, so opening `http://localhost:5173`
 * directly is the natural thing to do — and it has nowhere to go.
 *
 * Off unless `VITE_DEMO_MODE` is set, which `docker-compose.yml` does for the
 * local dev stack only. It must never be built into a deployed bundle: it lets
 * the page mint its own sessions (see `createDemoSession`).
 */
export const DEMO_MODE: boolean =
  (import.meta.env.VITE_DEMO_MODE as string | undefined) === "1" ||
  (import.meta.env.VITE_DEMO_MODE as string | undefined) === "true";

/**
 * Create a session and return its token — the browser standing in for the
 * merchant backend that would normally call `POST /v1/sessions` server-side.
 *
 * This is a *merchant* endpoint, not a session endpoint: it is deliberately not
 * a `VerifyApi` method, and it is only ever called behind `DEMO_MODE`. Against
 * a real deployment it needs an API key the browser must never hold.
 */
export async function createDemoSession(steps?: StepId[]): Promise<string> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${API_URL}/v1/sessions`, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone: "+15551234567",
        merchantName: "InertiaX Demo",
        // The local SMS provider just logs; no message is actually sent.
        ...(steps ? { steps } : {}),
      }),
    });
  } catch (err) {
    const timedOut = err instanceof DOMException && err.name === "AbortError";
    throw new ApiClientError(
      timedOut ? "TIMEOUT" : "NETWORK_ERROR",
      timedOut
        ? "The connection is too slow right now. Check your signal and try again."
        : "We couldn't reach the server. Check your connection and try again.",
      0,
    );
  } finally {
    clearTimeout(timer);
  }
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiClientError(
      typeof body.errorCode === "string" ? body.errorCode : "UNKNOWN",
      typeof body.detail === "string"
        ? body.detail
        : typeof body.title === "string"
          ? body.title
          : `HTTP ${response.status}`,
      response.status,
    );
  }

  // Take the token out of the returned link rather than navigating to the link
  // itself: it is built from the API's own VERIFY_APP_URL, which may not be the
  // origin this page is being served from (LAN IP, tunnel, different port).
  const link = typeof body.link === "string" ? body.link : "";
  const token = link.includes("#") ? tokenFromHash(link.slice(link.indexOf("#"))) : null;
  if (!token) {
    throw new ApiClientError("UNKNOWN", "The API did not return a usable link.", 500);
  }
  return token;
}

/** Parse the session token out of the hash route `#/s/<token>`. */
export function tokenFromHash(hash: string): string | null {
  const match = /^#\/s\/(.+)$/.exec(hash);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "") ??
  "http://localhost:4000";
